import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { parseSocketEvent } from '@/lib/parse-socket-event'

import { getWebSocketConfig, resolveWebSocketBaseUrl, WebSocketMessageAction } from '@/config/websocket.config'
import { isFsdsHostname } from '@/utils/domain-branding-resolver'
import { buildUserWebSocketChannel, getTenantFromHost } from '@/utils/tenant.utils'

import { useAuth } from './auth-context'
import { useTenant } from './tenant-context'

export { parseSocketEvent } from '@/lib/parse-socket-event'

interface SocketContextType {
  socket: WebSocket | null
  isConnected: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subscribe: (eventName: string, callback: (data: any) => void) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  unsubscribe: (eventName: string, callback: (data: any) => void) => void
}

const SocketContext = createContext<SocketContextType | undefined>(undefined)

interface SocketProviderProps {
  children: ReactNode
}

export function SocketProvider({ children }: SocketProviderProps) {
  const [isConnected, setIsConnected] = useState(false)
  const socketRef = useRef<WebSocket | null>(null)
  const { user } = useAuth()
  const { tenantId } = useTenant()
  const manuallyClosedRef = useRef(false)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const connectSocketRef = useRef<(() => void) | null>(null)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const listenersRef = useRef<Record<string, Set<(data: any) => void>>>({})

  // subscribe/unsubscribe methods
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const subscribe = useCallback((eventName: string, callback: (data: any) => void) => {
    if (!listenersRef.current[eventName]) {
      listenersRef.current[eventName] = new Set()
    }
    listenersRef.current[eventName].add(callback)
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const unsubscribe = useCallback((eventName: string, callback: (data: any) => void) => {
    const s = listenersRef.current[eventName]
    if (s) {
      s.delete(callback)
      if (s.size === 0) {
        delete listenersRef.current[eventName]
      }
    }
  }, [])

  useEffect(() => {
    const clearReconnectTimer = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }

    const clearHeartbeat = () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
        heartbeatIntervalRef.current = null
      }
    }

    const scheduleReconnect = (hostname: string | null) => {
      if (manuallyClosedRef.current || !user) {
        return
      }

      const config = getWebSocketConfig(hostname)
      if (reconnectAttemptsRef.current >= config.maxReconnectAttempts) {
        return
      }

      reconnectAttemptsRef.current += 1
      const delay = config.reconnectInterval * 2 ** (reconnectAttemptsRef.current - 1)

      clearReconnectTimer()
      reconnectTimeoutRef.current = setTimeout(() => {
        connectSocketRef.current?.()
      }, delay)
    }

    const dispatchSocketEvent = (eventName: string, data: unknown) => {
      const subs = listenersRef.current[eventName]
      if (!subs) {
        return
      }

      subs.forEach(cb => {
        try {
          cb(data)
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`Error in listener for event ${eventName}`, err)
        }
      })
    }

    const connectSocket = () => {
      if (!user) {
        return
      }

      const hostname = typeof window !== 'undefined' ? window.location.hostname : null
      const tenantSubdomain =
        tenantId && tenantId !== 'default'
          ? tenantId
          : hostname
            ? getTenantFromHost(hostname)
            : 'default'

      if (hostname && !isFsdsHostname(hostname) && tenantSubdomain === 'default') {
        return
      }

      if (socketRef.current) {
        socketRef.current.close()
        socketRef.current = null
      }

      const channel = buildUserWebSocketChannel(user.id, hostname, tenantSubdomain)
      const baseUrl = resolveWebSocketBaseUrl(hostname)
      const config = getWebSocketConfig(hostname)
      const socket = new WebSocket(
        `${baseUrl}?channel=${encodeURIComponent(channel)}&userId=${user.id}`
      )

      socketRef.current = socket

      socket.onopen = () => {
        // eslint-disable-next-line no-console
        console.log('Connected to WebSocket API')
        reconnectAttemptsRef.current = 0
        setIsConnected(true)
        socket.send(JSON.stringify({ action: 'sendMessage', data: 'Hello from Next.js' }))

        clearHeartbeat()
        heartbeatIntervalRef.current = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(
              JSON.stringify({
                action: WebSocketMessageAction.PING,
                data: { timestamp: Date.now() },
              })
            )
          }
        }, config.heartbeatInterval)
      }

      socket.onmessage = event => {
        try {
          const { eventName, data } = parseSocketEvent(event.data)
          if (!eventName || eventName === WebSocketMessageAction.PONG || eventName === 'connected') {
            return
          }

          dispatchSocketEvent(eventName, data)
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to parse WebSocket message as JSON', err)
        }
      }

      socket.onclose = event => {
        // eslint-disable-next-line no-console
        console.log('Disconnected')
        clearHeartbeat()
        setIsConnected(false)

        if (!manuallyClosedRef.current && event.code !== 1000) {
          scheduleReconnect(hostname)
        }
      }

      socket.onerror = err => {
        // eslint-disable-next-line no-console
        console.error('WebSocket error', err)
      }
    }

    connectSocketRef.current = connectSocket

    if (!user) {
      manuallyClosedRef.current = true
      clearReconnectTimer()
      clearHeartbeat()
      if (socketRef.current) {
        socketRef.current.close()
        socketRef.current = null
        setIsConnected(false)
      }
      return () => {
        connectSocketRef.current = null
      }
    }

    manuallyClosedRef.current = false
    reconnectAttemptsRef.current = 0
    connectSocket()

    return () => {
      manuallyClosedRef.current = true
      clearReconnectTimer()
      clearHeartbeat()
      connectSocketRef.current = null
      if (socketRef.current) {
        socketRef.current.close(1000)
        socketRef.current = null
      }
      setIsConnected(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tenantId])

  return (
    <SocketContext.Provider value={{ socket: socketRef.current, isConnected, subscribe, unsubscribe }}>
      {children}
    </SocketContext.Provider>
  )
}

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider')
  }
  return context
}
